import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Building2, MapPin, ArrowRight, Loader2, QrCode } from "lucide-react";
import { toast } from "sonner";

export default function MyBusinessPage() {
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/portal/businesses")
      .then((r) => setBusinesses(r.data.businesses || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-6" data-testid="my-business-page">
      <header>
        <p className="label-eyebrow">Firmen</p>
        <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">
          Meine Firma{businesses.length > 1 ? "n" : ""}
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Alle Firmen, die dir zugewiesen sind.
        </p>
      </header>

      {loading ? (
        <div className="card-surface py-10 text-center text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Lädt…
        </div>
      ) : businesses.length ? (
        <div className="grid md:grid-cols-2 gap-4">
          {businesses.map((b) => (
            <div
              key={b.id}
              className="card-surface p-5 flex flex-col gap-3"
              data-testid={`business-card-${b.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <Building2 className="w-4 h-4 text-primary shrink-0" />
                  <h3 className="font-heading font-semibold truncate">
                    {b.business_name}
                  </h3>
                </div>
                <Badge
                  variant="outline"
                  className={
                    b.status === "active"
                      ? "border-emerald-500/30 text-emerald-400 bg-emerald-500/10"
                      : "border-white/10 text-muted-foreground"
                  }
                >
                  {b.status === "active" ? "Aktiv" : "Inaktiv"}
                </Badge>
              </div>
              <div className="text-sm text-muted-foreground flex items-start gap-2">
                <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>{b.address}</span>
              </div>

              <div className="grid grid-cols-3 gap-2 mt-1">
                <MiniStat label="Scans" value={b.total_scans} />
                <MiniStat label="Heute" value={b.scans_today} tone="primary" />
                <MiniStat
                  label="Rating"
                  value={
                    typeof b.google_rating === "number" ? b.google_rating.toFixed(1) : "—"
                  }
                />
              </div>

              <div className="flex gap-2 mt-2">
                <Link to={`/customer/business/${b.id}`} className="flex-1" data-testid={`portal-business-link-${b.id}`}>
                  <Button className="w-full rounded-full gap-2" data-testid={`open-business-${b.id}`}>
                    <QrCode className="w-4 h-4" /> QR & Details
                  </Button>
                </Link>
                <Link to={`/customer/business/${b.id}`} className="hidden sm:inline">
                  <Button variant="ghost" size="icon" className="rounded-full">
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card-surface p-8 text-center text-sm text-muted-foreground">
          Dir wurden noch keine Firmen zugewiesen. Bitte kontaktiere deinen
          Ansprechpartner.
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, tone }) {
  return (
    <div className="rounded-lg bg-white/[0.02] py-2 text-center">
      <div
        className={`font-heading font-semibold text-base ${
          tone === "primary" ? "text-primary" : ""
        }`}
      >
        {value ?? "—"}
      </div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
        {label}
      </div>
    </div>
  );
}
